import { isUnsupportedColorExportError } from "@/lib/projectMarkupExport";
import { PROPOSAL_PDF_TOO_LARGE_MESSAGE } from "@/lib/proposalPdfExportScale";

export type ProposalPdfExportFailureKind =
  | "unsupported_styling"
  | "image_cors"
  | "memory"
  | "assembly"
  | "unknown";

export const PROPOSAL_PDF_EXPORT_CUSTOMER_MESSAGES: Record<ProposalPdfExportFailureKind, string> = {
  unsupported_styling:
    "PDF export encountered an unsupported visual element. Please refresh and try again.",
  image_cors: "We couldn’t generate the proposal PDF because some images could not be loaded. Please try again.",
  memory: PROPOSAL_PDF_TOO_LARGE_MESSAGE,
  assembly: "We couldn’t assemble the proposal PDF. Please try again.",
  unknown: "We couldn’t generate the proposal PDF. Please try again.",
};

export type ProposalPdfExportFailureContext = {
  pageIndex?: number;
  pageCount?: number;
  pageTestId?: string | null;
  proposalRef?: string | null;
};

export function classifyProposalPdfExportError(
  error: unknown,
  context: ProposalPdfExportFailureContext = {},
): ProposalPdfExportFailureKind {
  if (error instanceof Error && error.message === PROPOSAL_PDF_TOO_LARGE_MESSAGE) {
    return "memory";
  }

  const message = error instanceof Error ? error.message : String(error ?? "");
  const lower = message.toLowerCase();

  if (isUnsupportedColorExportError(error) || /unsupported color function|oklch|oklab|color-mix/i.test(message)) {
    return "unsupported_styling";
  }

  if (
    /tainted|cross-origin|cors|securityerror|unable to get image data|failed to execute 'todataurl'/i.test(
      message,
    )
  ) {
    return "image_cors";
  }

  if (/out of memory|\boodm\b|allocation failed|canvas.*too (large|big)|maximum call stack/i.test(lower)) {
    return "memory";
  }

  if (/jspdf|addimage|pdf\.save|invalid string|pdf assembly/i.test(lower)) {
    return "assembly";
  }

  // Preserve known hard-limit message phrasing.
  if (context.pageCount != null && /too large to generate reliably/i.test(message)) {
    return "memory";
  }

  return "unknown";
}

export function customerMessageForProposalPdfExportError(
  error: unknown,
  context: ProposalPdfExportFailureContext = {},
): string {
  // Keep explicit pre-thrown customer messages when already safe.
  if (error instanceof Error) {
    if (error.message === PROPOSAL_PDF_TOO_LARGE_MESSAGE) return error.message;
    if (error.message === "Proposal PDF is not ready yet. Please try again in a moment.") return error.message;
    if (error.message === "No printable proposal pages were found.") return error.message;
    if (error.message === "A proposal PDF is already being generated. Please wait for it to finish.") {
      return error.message;
    }
  }

  const kind = classifyProposalPdfExportError(error, context);
  return PROPOSAL_PDF_EXPORT_CUSTOMER_MESSAGES[kind];
}

/** Dev-only: log the underlying html2canvas / export exception with page context. */
export function logProposalPdfExportFailure(
  error: unknown,
  context: ProposalPdfExportFailureContext & {
    kind?: ProposalPdfExportFailureKind;
    unsupportedHits?: Array<{ selector: string; property: string; value: string }>;
  },
): void {
  if (!import.meta.env.DEV) return;

  const err = error instanceof Error ? error : new Error(String(error ?? "Unknown export failure"));
  const kind = context.kind ?? classifyProposalPdfExportError(error, context);

  console.error("[proposal-pdf-export] capture failed", {
    kind,
    name: err.name,
    message: err.message,
    stack: err.stack,
    pageIndex: context.pageIndex,
    pageCount: context.pageCount,
    pageTestId: context.pageTestId,
    proposalRef: context.proposalRef,
    unsupportedHits: context.unsupportedHits?.slice(0, 12),
  });
}
